import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInsightsCtx } from "../../pages/Events/InsightsLayout";
import {
  getUserConfig,
  putUserConfig,
  type UserConfigDto,
} from "../../services/userConfig";
import type { TempUnit } from "../../services/cookingState";
import UserConfigEditor from "./UserConfigEditor";
import { isAmbientBandValid } from "./HomeTempThresholdEditor";
import Button from "./Button";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type Props = {
  sensorName: string;
  unit: TempUnit;
};

/**
 * Full per-sensor UserConfig editor for the SensorDetailPage. Loads
 * the sensor's current config, seeds the editor's sections as ticked
 * with those values (so the user can just change one and save), then
 * PUTs a partial patch containing exactly the fields ticked at save
 * time.
 *
 * Different shape from [SensorHomeTempActivityRow], which shows the
 * live ambient reading + inline threshold edit as a compact row in
 * "Recent Activity". This card sits alongside that row and lets the
 * user reach every other UserConfig knob without opening the phone
 * app.
 *
 * Demo Mode is deliberately not surfaced -- it is install-time only
 * on the sensor and not reachable over the cloud API.
 */
export default function SensorUserConfigCard({ sensorName, unit }: Props) {
  const queryClient = useQueryClient();
  const { registerUnsavedGuard } = useInsightsCtx();

  // Starts collapsed: these are settings somebody sets once, and left open
  // they push the alert history and charts below the fold on every visit.
  const [open, setOpen] = useState(false);

  // Mounted on FIRST open and kept mounted after that, rather than unmounted
  // on collapse. UserConfigEditor seeds its working state once at mount and
  // never resyncs, so unmounting would silently discard a half-finished edit
  // the moment someone folded the card away. Staying mounted also keeps the
  // unsaved-changes guards below armed while it is shut.
  const [everOpened, setEverOpened] = useState(false);
  const bodyId = `sensor-config-${sensorName}`;

  const { data: current, isPending, isError, refetch } = useQuery({
    queryKey: ["userConfig", sensorName],
    queryFn: async () => await getUserConfig(sensorName),
    // Nothing is fetched until the card is opened, so a sensor page load no
    // longer pays for settings most visits never look at.
    enabled: !!sensorName && everOpened,
    // Infinity, not 30s. UserConfigEditor seeds its working state ONCE at
    // mount and never resyncs (no useEffect in that file, deliberately -- a
    // prop change must not wipe in-flight toggles). Before cts_api#56 a
    // background refetch returned the same bytes, so ignoring it cost nothing.
    // Since #56 the read-path reconcile can adopt the DEVICE's values, so a
    // second GET can legitimately differ -- and the editor would keep its
    // mount-time seeds and write the pre-reconcile values back over them on
    // save, silently. This card owns the data and invalidates explicitly after
    // a save, so there is nothing a background refetch adds here.
    staleTime: Infinity,
    retry: false,
  });

  const [patch, setPatch] = useState<UserConfigDto>({});

  // This card had NO validity check: the editor rendered "The minimum must be
  // lower than the maximum" and Save stayed enabled, so a zero-width ambient
  // band saved and published to the device. The bulk modal had its own copy of
  // this; both now use the editor's exported rule.
  const invalid =
    !!patch.ambientAlerts &&
    !isAmbientBandValid(patch.ambientAlerts.lowC, patch.ambientAlerts.highC);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: async (p: UserConfigDto) =>
      await putUserConfig(sensorName, p),
    onSuccess: () => {
      setSaved(true);
      setPatch({});
      queryClient.invalidateQueries({
        queryKey: ["userConfig", sensorName],
      });
      // The ambient row shares the endpoint; refresh it too so the
      // range reflects any change made here.
      queryClient.invalidateQueries({
        queryKey: ["homeTemperatureThreshold", sensorName],
      });
    },
  });

  // Every section with a value in the persisted config starts ticked
  // so the user can just edit-and-save. On a fresh sensor with nothing
  // set, sections start unticked (bulk-modal style) so nothing gets
  // pushed accidentally.
  const initiallyTicked: Array<
    "ambientAlerts" | "unattendedSpeed" | "auxLights" | "speakerVolume" | "buttonLockout"
  > = [];
  if (current?.ambientAlerts) initiallyTicked.push("ambientAlerts");
  if (current?.unattendedSpeed != null) initiallyTicked.push("unattendedSpeed");
  if (current?.auxLights != null) initiallyTicked.push("auxLights");
  if (current?.speakerVolume != null) initiallyTicked.push("speakerVolume");
  if (current?.buttonLockout != null) initiallyTicked.push("buttonLockout");

  const nothingToApply = Object.keys(patch).length === 0;
  const dirty = !nothingToApply;

  // Register this card's dirty state with the InsightsLayout so its
  // "Back to Sensors" / "Back to Properties" button can prompt before
  // navigating away. Guard fn reads through a ref so we register once
  // and the ref always exposes the latest dirty flag without churning
  // add/delete on every render.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    return registerUnsavedGuard(() => dirtyRef.current);
  }, [registerUnsavedGuard]);

  // Guard tab close / reload / typing a different URL in the bar.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // Guard the browser Back button. react-router-dom 6.2 on this repo
  // doesn't ship useBlocker on the non-data router, so we intercept
  // via popstate:
  //   1. When we become dirty, push a duplicate history entry (same
  //      URL, so the user still sees this page).
  //   2. On popstate (Back was pressed and the browser moved into our
  //      duplicate) prompt.  If confirmed, go back one more to leave
  //      the page; if cancelled, re-push the duplicate to re-arm.
  // The InsightsLayout "Back to …" button is covered separately via
  // registerUnsavedGuard above. Other in-app links (sidebar / navbar
  // navigation) are NOT caught -- those don't trigger popstate and
  // don't go through the layout's back button. A durable fix is a
  // router upgrade to a data router + useBlocker.
  useEffect(() => {
    if (!dirty) return;

    const url = window.location.href;
    window.history.pushState(null, "", url);

    const onPopState = () => {
      const ok = window.confirm(
        "Discard your unsaved changes?\n\n" +
          "You've picked settings to change but haven't saved them yet. " +
          "Leaving this page will lose your selections."
      );
      if (ok) {
        // Pop past the original entry too so the user actually leaves.
        window.history.go(-1);
      } else {
        // Re-arm so the next Back press prompts again.
        window.history.pushState(null, "", url);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // NOTE: if the user leaves via a non-popstate route (sidebar link),
    // our placeholder history entry leaks and would need one extra Back
    // press to skip on a return visit. Minor and worth the simplicity
    // until the router upgrade lands.
  }, [dirty]);

  return (
    <div className="rounded-card bg-white text-ink p-5 shadow-depth border border-black/5">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => {
          setEverOpened(true);
          setOpen((v) => !v);
        }}
        className={[
          "flex items-center gap-3 w-full text-left rounded-card",
          "focus-visible:outline focus-visible:outline-2",
          "focus-visible:outline-offset-2 focus-visible:outline-alert-info",
          open ? "mb-4" : "",
        ].join(" ")}
      >
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-alert-info/10 text-alert-info">
          <TuneIcon sx={{ fontSize: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold">Sensor Configuration</div>
          <div className="text-xs text-ink-3">
            {open
              ? "Tick a section to change it. Untouched sections are preserved."
              : "Alert speed, lights, volume, button lock and temperature range."}
          </div>
        </div>
        {/*
          Collapsing hides the Save button, so a dirty card would look settled
          and only announce itself when the user tried to leave the page. Say
          it on the header instead.
        */}
        {dirty && !open && (
          <span className="shrink-0 text-xs font-semibold text-alert-warning">
            Unsaved changes
          </span>
        )}
        <ExpandMoreIcon
          className="shrink-0 text-ink-3 transition-transform"
          sx={{ fontSize: 22, transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>

      <div id={bodyId} hidden={!open}>
      {!everOpened ? null : isPending ? (
        <div className="text-sm text-ink-3 py-4">Loading current settings…</div>
      ) : isError ? (
        <div className="flex items-center justify-between py-3">
          <span className="text-sm text-alert-critical">
            Couldn't load the sensor's current settings.
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <UserConfigEditor
            initial={current ?? null}
            initiallyTicked={initiallyTicked}
            onChange={(next) => {
              setSaved(false);
              setPatch(next);
            }}
            unit={unit}
            disabled={save.isPending}
          />
          <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-black/10">
            {save.isPending && (
              <span className="text-ink-3 text-xs">
                Sending to device…
              </span>
            )}
            {save.isError && !save.isPending && (
              <span className="text-alert-critical text-xs">
                Couldn't reach the device. Try again.
              </span>
            )}
            {saved && !save.isPending && !save.isError && (
              <span className="text-alert-success text-xs">Saved.</span>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => save.mutate(patch)}
              disabled={nothingToApply || invalid || save.isPending}
              title={
                nothingToApply
                  ? "Tick a section and edit it to enable Save"
                  : undefined
              }
            >
              Save Changes
            </Button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
